import React, { useEffect, useState } from "react";
import { FormFieldError } from "components/shared-ui/form/form-field-error/form-field-error";
import { FormField } from "components/shared-ui/form/form-field/form-field";
import { validators } from "utils/validators";
import { IIAMPolicyCreateRequest } from "api/entities/iam-policies";
import CodeMirror from "@uiw/react-codemirror";
import { json, jsonParseLinter } from "@codemirror/lang-json";
import { Diagnostic, linter, LintSource } from "@codemirror/lint";
import { abyss } from "@uiw/codemirror-theme-abyss";
import Button from "components/shared-ui/button";
import Paper from "@mui/material/Paper";
import SidePanelSection from "components/shared-ui/side-panel-section/side-panel-section";
export type IIAMPolicyDocumentSectionErrors = {
  PolicyDocument: string;
};

export interface IIAMPolicyDescriptionSectionProps {
  value: string;
  errors: IIAMPolicyDocumentSectionErrors;
  isPolicyCreation: boolean;
  onChange: (document: Partial<IIAMPolicyCreateRequest>) => void;
  onError: (errors: IIAMPolicyDocumentSectionErrors) => void;
  className?: string;
  readonly?: boolean;
}

function createJsonLinter(
  onDiagnostics?: (diagnostics: Diagnostic[]) => void,
): LintSource {
  const baseLinter = jsonParseLinter();

  return (view) => {
    const diagnostics = baseLinter(view);
    if (diagnostics.length > 0) {
      onDiagnostics?.(diagnostics);
    }
    return diagnostics;
  };
}

export function IAMPolicyDocumentSection(
  props: IIAMPolicyDescriptionSectionProps,
) {
  const [editing, setEditing] = useState(false);

  const handleChange = (document: string) => {
    props.onChange({ PolicyDocument: document });

    props.onError({
      PolicyDocument: validators.validateIAMPolicyDocument(document),
    });
  };

  const handleDiagnostics = (diagnostics: Diagnostic[]) => {
    props.onError({ PolicyDocument: diagnostics[0].message });
  };

  const formatDoc = (doc: string): string => {
    try {
      const obj = JSON.parse(doc.trim());
      const formatted = JSON.stringify(obj, null, 2);

      return formatted;
    } catch (e) {
      return doc;
    }
  };

  useEffect(() => {
    if (!props.value || editing) return;

    const formatted = formatDoc(props.value);
    props.onChange({ PolicyDocument: formatted });

    // eslint-disable-next-line
  }, [props.value, editing]);

  return (
    <SidePanelSection title="Policy">
      <Paper
        variant="outlined"
        sx={{
          padding: "10px",
          height: "400px",
        }}
      >
        <FormField
          testId={`iam-policy-document`}
          label="Document Editor"
          className={props.className}
          visible={true}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <CodeMirror
              value={props.value}
              basicSetup={{
                foldGutter: false,
              }}
              editable={(editing || props.isPolicyCreation) && !props.readonly}
              height="300px"
              theme={abyss}
              extensions={[
                json(),
                linter(createJsonLinter(handleDiagnostics), {
                  tooltipFilter: () => [],
                  markerFilter: () => [],
                }),
              ]}
              onChange={handleChange}
            />
            {!props.isPolicyCreation && !editing && !props.readonly && (
              <Button
                testId="edit-policy-button"
                style={{ alignSelf: "flex-end", margin: "4px" }}
                colors="primary"
                onClick={() => {
                  setEditing(true);
                }}
              >
                Edit
              </Button>
            )}
          </div>
          <FormFieldError error={props.errors?.PolicyDocument} />
        </FormField>
      </Paper>
    </SidePanelSection>
  );
}
